import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

// ANT DESIGN IMPORTS
import {
  Input,
  Row,
  Typography,
  Card,
  Button,
  Modal,
  Form,
  Col,
  Table,
  Spin,
  Select,
  Divider,
  Tag,
} from "antd";

import { ArrowDownOutlined, CheckCircleOutlined } from "@ant-design/icons";

// PROJECT IMPORTS
import MainLayout from "../../../components/layouts/MainLayout";
import TopContent from "../../../components/layouts/TopContent";
import { BOM } from "../../../services/definedMethod";
import cnt from "../../../services/definedConstant";
import DownloadTableButtons from "../../../components/DownloadTableButtons";
import { rightAction } from "../../../redux/actions";
import { apiConfig, labelConfig, staticConfig } from "../security.config";
import SortableColumn from "../../../components/SorterTableColumn";

const { Text, Title } = Typography;

const columns = [
  SortableColumn({
    label: labelConfig.name,
    dataIndex: "name",
    sorter: (a, b) => a.name.localeCompare(b.name),
  }),
  SortableColumn({
    label: labelConfig.isLoanOfficer,
    dataIndex: "isLoanOfficer",
    sorter: (a, b) => a.isLoanOfficer.localeCompare(b.isLoanOfficer),
  }),
  SortableColumn({
    label: labelConfig.branch,
    dataIndex: "branch",
    sorter: (a, b) => a.branch.localeCompare(b.branch),
  }),
  {
    title: "Status",
    dataIndex: "status",
  },
];

const ManageEmployees = (props) => {
  //DECLARED VARIABLES
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [tableCount, setTableCount] = useState("10");
  const [employeesList, setEmployeesList] = useState([]);
  const [originalList, setOriginalList] = useState([]);

  useEffect(() => {
    BOM.HasPermission("READ_STAFF") && getEmployeesList();
    BOM.HasPermission("CREATE_STAFF") && rightAction(cnt.RIGHT_ADD_BTN);
  }, []);

  const getEmployeesList = () => {
    const params = {
      status: "all",
    };
    let body = {
      session,
      params,
    };

    BOM.FetchReqAction(body, apiConfig.Staff, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.errors[0]?.defaultUserMessage);
      } else {
        const objList = res;
        let tempEmployeesList = [];

        objList.forEach((element) => {
          tempEmployeesList.push({
            key: element?.id,
            id: element?.id,
            name: element?.displayName ? element?.displayName : "-",
            isLoanOfficer: element?.isLoanOfficer ? "Yes" : "No",
            branch: element?.officeName ? element?.officeName : "-",
            status: element?.isActive ? (
              <Tag icon={<CheckCircleOutlined />} color={"success"}>
                Active
              </Tag>
            ) : (
              <Tag icon={<CheckCircleOutlined />} color={"red"}>
                Inactive
              </Tag>
            ),
          });
        });

        setEmployeesList(tempEmployeesList);
        setOriginalList(tempEmployeesList);

        //STOP LOAD
        setIsLoading(false);
      }
    });
  };

  const getCsvListData = () => {
    return new Promise((resolve, reject) => {
      const params = {
        status: "all",
        limit: 1000,
        offset: 0,
      };

      let body = {
        session,
        params,
      };

      BOM.FetchReqAction(body, apiConfig.Staff, (err, res) => {
        if (err) {
          reject(err);
        } else {
          const objList = res;
          let tempEmployeesList = [];

          objList.forEach((element) => {
            tempEmployeesList.push({
              key: element?.id,
              id: element?.id,
              name: element?.displayName ? element?.displayName : "-",
              isLoanOfficer: element?.isLoanOfficer ? "Yes" : "No",
              branch: element?.officeName ? element?.officeName : "-",
              status: element?.isActive ? (
                <Tag icon={<CheckCircleOutlined />} color={"success"}>
                  Active
                </Tag>
              ) : (
                <Tag icon={<CheckCircleOutlined />} color={"red"}>
                  Inactive
                </Tag>
              ),
            });
          });

          resolve(tempEmployeesList);
        }
      });
    });
  };

  //DECLARED METHODS
  const onSelectItemAction = (selectedRowKeys, objParam) => {
    //HOLD THE OBJECT SELECTED HERE
    setSelectedRow(objParam[0]);
    [
      BOM.HasPermission("READ_STAFF") && cnt.RIGHT_DETAIL_BTN,
      BOM.HasPermission("UPDATE_STAFF") && cnt.RIGHT_EDIT_BTN,
    ].map((item) => {
      dispatch({
        type: item,
      });
    });
  };
  function handleSearchInTable(searchKey) {
    searchKey = searchKey.target.value;

    if (!searchKey) {
      setEmployeesList(originalList);
      return;
    }
    const filterBySearch = originalList.filter((item) =>
      item?.name?.toLowerCase().includes(searchKey?.toLowerCase())
    );
    setEmployeesList(filterBySearch);
  }
  function selectTableCount(value) {
    setTableCount(value);
  }
  function handleOnView() {
    const { detailBtn } = rightBtn;
    if (detailBtn.toLowerCase() === "primary") {
      navigate("/view-employee", { state: { id: selectedRow.id } });
    } else {
      console.log("Could not open the link");
    }
  }
  function handleOnCreate() {
    const { addBtn } = rightBtn;
    if (addBtn.toLowerCase() === "primary") {
      navigate("/add-employee", { ofObj: selectedRow });
    } else {
      console.log("Could not open the link");
    }
  }
  function handleOnEdit() {
    const { editBtn } = rightBtn;
    if (editBtn.toLowerCase() === "primary") {
      navigate("/edit-employee", { state: { id: selectedRow.id } });
    } else {
      console.log("Please choose a record from the table to open");
    }
  }

  return (
    <MainLayout
      isEnableRightSidebar={true}
      onView={handleOnView}
      onAdd={handleOnCreate}
      onEdit={handleOnEdit}
    >
      <TopContent
        pageTitle={labelConfig.manageEmployees}
        pageItems={staticConfig.manageEmployeesBreadcrumbItems}
      />

      <Card
        title={`${labelConfig.employee} List`}
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
        }}
      >
        {/* RENDER TABLE LIST HERE*/}
        <Spin spinning={isLoading} size="large">
          <Row style={{ marginBottom: 20 }}>
            <div style={{ marginBottom: "auto" }}>
              <Text style={{ marginRight: 8 }}>Show</Text>
              <Select
                defaultValue={tableCount}
                options={[
                  { value: "5", label: "5" },
                  { value: "10", label: "10" },
                  { value: "25", label: "25" },
                  { value: "50", label: "50" },
                ]}
                onSelect={selectTableCount}
              />
              <Text style={{ marginLeft: 8 }}>Entries</Text>
            </div>

            <div style={{ margin: "0px 50px" }}>
              <Input
                onChange={handleSearchInTable}
                placeholder="Search by Name"
                style={{
                  width: 400,
                }}
                allowClear
              />
            </div>
            {/* <DownloadTableButtons
              downloadData={employeesList}
              columns={columns}
              fileName={"Employees List"}
              onExportClick={getCsvListData}
            /> */}
          </Row>
          <Table
            rowSelection={{
              type: "radio",
              onChange: onSelectItemAction,
            }}
            columns={columns}
            dataSource={employeesList}
            bordered
            pagination={{
              position: "bottomCentre",
              pageSize: tableCount,
              showSizeChanger: false,
            }}
          />
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = { rightAction };
export default connect(mapStateToProps, mapDispatchToProps)(ManageEmployees);
